import React, { useState, useEffect, useMemo } from "react";
import { Link, Redirect } from "react-router-dom";
import { Row, Col } from "../../../components/Component";
import { Card, Form, FormGroup, Label, Input } from "reactstrap";
import swal from "sweetalert";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";

// ─── helpers (Quick Links checklist) ───────────────────────────────────────────

/** Credentials present and integration not turned off in Admin */
function integrationReady(val) {
  if (!val || typeof val !== "object") return false;
  if (val.enabled === false) return false;
  return !!val.configured;
}


// ─── Tab 2 – Teleconsultation ─────────────────────────────────────────────────

export function TeleconsultationTab() {
  const token = useMemo(() => localStorage.getItem("accessToken"), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState({
    tc_otp_enabled: false,
    tc_cancellation_hours_before: 24,
    tc_refund_percentage: 100,
    tc_platform_commission_percent: 15,
    documentRetentionYears: 5,
    tc_default_duration_minutes: 20,
    support_email: "",
    fx_reference_currency: "EUR",
    fx_rate_source: "frankfurter",
    fx_refresh_minutes: 60,
    fx_display_currencies: "EUR,USD,GBP,CHF",
  });

  useEffect(() => {
    http
      .get("/api/v1/settings", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data?.data || [];
        const map = {};
        data.forEach((s) => { map[s.key] = s.value; });

        setFields({
          tc_otp_enabled: map.tc_otp_enabled === true || map.tc_otp_enabled === "true" || map.tc_otp_enabled === 1 || map.tc_otp_enabled === "1",
          tc_cancellation_hours_before: Number.isFinite(Number(map.tc_cancellation_hours_before)) ? Number(map.tc_cancellation_hours_before) : 24,
          tc_refund_percentage: Number.isFinite(Number(map.tc_refund_percentage)) ? Number(map.tc_refund_percentage) : 100,
          tc_platform_commission_percent: Number.isFinite(Number(map.tc_platform_commission_percent))
            ? Number(map.tc_platform_commission_percent)
            : 15,
          documentRetentionYears: Number.isFinite(Number(map.documentRetentionYears)) ? Number(map.documentRetentionYears) : 5,
          tc_default_duration_minutes: Number.isFinite(Number(map.tc_default_duration_minutes)) ? Number(map.tc_default_duration_minutes) : 20,
          support_email: map.support_email != null ? String(map.support_email) : "",
          fx_reference_currency: map.fx_reference_currency != null ? String(map.fx_reference_currency).toUpperCase() : "EUR",
          fx_rate_source: map.fx_rate_source != null ? String(map.fx_rate_source) : "frankfurter",
          fx_refresh_minutes: Number.isFinite(Number(map.fx_refresh_minutes)) ? Number(map.fx_refresh_minutes) : 60,
          fx_display_currencies:
            map.fx_display_currencies != null ? String(map.fx_display_currencies) : "EUR,USD,GBP,CHF",
        });
      })
      .catch(() => swal("Error", "Failed to load teleconsultation settings", "error"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFields((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await http.put(
        "/api/v1/settings/bulk",
        {
          settings: [
            { key: "tc_otp_enabled", value: fields.tc_otp_enabled, label: "Teleconsultation OTP enabled", category: "teleconsultation", type: "boolean", isPublic: false },
            { key: "tc_cancellation_hours_before", value: Number(fields.tc_cancellation_hours_before) || 24, label: "TC cancellation deadline (hours)", category: "teleconsultation", type: "number", isPublic: false },
            { key: "tc_refund_percentage", value: Number(fields.tc_refund_percentage) ?? 100, label: "TC refund percentage", category: "teleconsultation", type: "number", isPublic: false },
            { key: "documentRetentionYears", value: Number(fields.documentRetentionYears) || 5, label: "Document retention (years)", category: "teleconsultation", type: "number", isPublic: false },
            { key: "tc_default_duration_minutes", value: Number(fields.tc_default_duration_minutes) || 20, label: "Default consultation duration (minutes)", category: "teleconsultation", type: "number", isPublic: false },
            {
              key: "support_email",
              value: String(fields.support_email || "").trim(),
              label: "Partner support inbox email",
              category: "teleconsultation",
              type: "string",
              isPublic: false,
            },
            {
              key: "fx_reference_currency",
              value: String(fields.fx_reference_currency || "EUR").trim().toUpperCase().slice(0, 3),
              label: "FX reference currency",
              category: "teleconsultation",
              type: "string",
              isPublic: true,
            },
            {
              key: "fx_rate_source",
              value: String(fields.fx_rate_source || "frankfurter").trim().toLowerCase(),
              label: "FX rate source",
              category: "teleconsultation",
              type: "string",
              isPublic: false,
            },
            {
              key: "fx_refresh_minutes",
              value: Number(fields.fx_refresh_minutes) || 60,
              label: "FX refresh interval (minutes)",
              category: "teleconsultation",
              type: "number",
              isPublic: false,
            },
            {
              key: "fx_display_currencies",
              value: String(fields.fx_display_currencies || "EUR,USD,GBP,CHF").trim(),
              label: "FX display currencies",
              category: "teleconsultation",
              type: "string",
              isPublic: true,
            },
          ],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      swal("Saved", "Teleconsultation settings saved successfully.", "success");
    } catch (err) {
      swal("Error", err.response?.data?.error || "Failed to save settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="card-bordered">
      <div className="card-inner">
        <Form>
          <Row className="g-4">
            <Col md="12">
              <FormGroup>
                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="tc_otp_enabled"
                    name="tc_otp_enabled"
                    checked={fields.tc_otp_enabled}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  <Label className="custom-control-label" htmlFor="tc_otp_enabled">
                    Enable OTP for video consultation access
                  </Label>
                </div>
                <span className="form-note">
                  When enabled, patients receive a one-time code in their confirmation email to access the video consultation.
                  Controlled here only (not via server environment variables).
                </span>
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label htmlFor="tc_cancellation_hours_before">Cancellation deadline (hours before appointment)</Label>
                <Input
                  type="number"
                  id="tc_cancellation_hours_before"
                  name="tc_cancellation_hours_before"
                  min={0}
                  value={fields.tc_cancellation_hours_before}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span className="form-note">Minimum hours before the appointment that a patient can cancel and be eligible for refund.</span>
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label htmlFor="tc_refund_percentage">Refund percentage (0–100)</Label>
                <Input
                  type="number"
                  id="tc_refund_percentage"
                  name="tc_refund_percentage"
                  min={0}
                  max={100}
                  value={fields.tc_refund_percentage}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span className="form-note">Percentage of the consultation price refunded when cancellation is within the allowed window.</span>
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label htmlFor="tc_platform_commission_percent">Platform commission on TC revenue (0–100)</Label>
                <Input
                  type="number"
                  id="tc_platform_commission_percent"
                  name="tc_platform_commission_percent"
                  min={0}
                  max={100}
                  step="0.01"
                  value={fields.tc_platform_commission_percent}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span className="form-note">
                  Applied to partner dashboard gross teleconsultation amounts for the Inoqare fee and net-to-partner estimate (same
                  period as paid/completed consultations). Configured here only — not via server env.
                </span>
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label htmlFor="documentRetentionYears">Document retention (years)</Label>
                <Input
                  type="number"
                  id="documentRetentionYears"
                  name="documentRetentionYears"
                  min={1}
                  value={fields.documentRetentionYears}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span className="form-note">Number of years to keep consultation documents before purge.</span>
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label htmlFor="tc_default_duration_minutes">Default consultation duration (minutes)</Label>
                <Input
                  type="number"
                  id="tc_default_duration_minutes"
                  name="tc_default_duration_minutes"
                  min={10}
                  max={60}
                  value={fields.tc_default_duration_minutes}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span className="form-note">Default duration (minutes) for teleconsultation slots (10–60).</span>
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label htmlFor="support_email">Partner support inbox email</Label>
                <Input
                  type="email"
                  id="support_email"
                  name="support_email"
                  autoComplete="email"
                  placeholder="support@example.com"
                  value={fields.support_email}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span className="form-note">
                  Receives partner portal support requests (calendar setup, technical issues, etc.).
                </span>
              </FormGroup>
            </Col>

            <Col md="12">
              <h6 className="mb-2">Find a Doctor — display currencies</h6>
              <p className="text-soft small mb-3">
                Patients can switch the list price to an indicative currency. Stripe always charges the clinic
                consultation currency (not converted). Rates come from the source below.
              </p>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label htmlFor="fx_reference_currency">Reference currency</Label>
                <Input
                  type="text"
                  id="fx_reference_currency"
                  name="fx_reference_currency"
                  maxLength={3}
                  value={fields.fx_reference_currency}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span className="form-note">Usually EUR. Rates are quoted against this code.</span>
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label htmlFor="fx_rate_source">Rate source</Label>
                <Input
                  type="select"
                  id="fx_rate_source"
                  name="fx_rate_source"
                  value={fields.fx_rate_source}
                  onChange={handleChange}
                  disabled={saving}
                >
                  <option value="frankfurter">Frankfurter (ECB)</option>
                </Input>
                <span className="form-note">Live mid-market rates for indicative display only.</span>
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label htmlFor="fx_refresh_minutes">Refresh (minutes)</Label>
                <Input
                  type="number"
                  id="fx_refresh_minutes"
                  name="fx_refresh_minutes"
                  min={5}
                  max={1440}
                  value={fields.fx_refresh_minutes}
                  onChange={handleChange}
                  disabled={saving}
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label htmlFor="fx_display_currencies">Displayable currencies</Label>
                <Input
                  type="text"
                  id="fx_display_currencies"
                  name="fx_display_currencies"
                  value={fields.fx_display_currencies}
                  onChange={handleChange}
                  disabled={saving}
                  placeholder="EUR,USD,GBP,CHF"
                />
                <span className="form-note">Comma-separated ISO codes shown in the patient selector.</span>
              </FormGroup>
            </Col>

            <Col md="12">
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save teleconsultation settings"}
              </button>
            </Col>
          </Row>
        </Form>
      </div>
    </Card>
  );
}

// ─── Platform (general) ─────────────────────────────────────────────────────

export function PlatformTab() {
  const token = useMemo(() => localStorage.getItem("accessToken"), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [platform, setPlatform] = useState({
    googlePlacesEnabled: false,
    googlePlacesApiKey: "",
    blurStrategyEnabled: false,
    adminLeadNotificationEmail: "",
    showSiteWipBanner: false,
  });

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    http
      .get("/api/v1/settings", { headers })
      .then((settingsRes) => {
        const data = settingsRes.data?.data || [];
        const map = {};
        data.forEach((s) => { map[s.key] = s.value; });
        setPlatform({
          googlePlacesEnabled: map.google_places_enabled === true || map.google_places_enabled === "true" || map.google_places_enabled === 1,
          googlePlacesApiKey: map.google_places_api_key || "",
          blurStrategyEnabled: map.blur_strategy_enabled === true || map.blur_strategy_enabled === "true" || map.blur_strategy_enabled === 1,
          adminLeadNotificationEmail: map.admin_lead_notification_email || "",
          showSiteWipBanner:
            map.show_site_wip_banner === true ||
            map.show_site_wip_banner === "true" ||
            map.show_site_wip_banner === 1 ||
            map.show_site_wip_banner === "1",
        });
      })
      .catch(() => swal("Error", "Failed to load platform settings", "error"))
      .finally(() => setLoading(false));
  }, [token]);

  const handlePlatformChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPlatform((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handlePlatformSave = async () => {
    try {
      setSaving(true);
      await http.put(
        "/api/v1/settings/bulk",
        {
          settings: [
            { key: "google_places_enabled", value: platform.googlePlacesEnabled, label: "Google Places enabled", category: "feature", type: "boolean", isPublic: true },
            { key: "google_places_api_key", value: platform.googlePlacesApiKey, label: "Google Places API Key", category: "api", type: "string", isPublic: false },
            { key: "blur_strategy_enabled", value: platform.blurStrategyEnabled, label: "Blur strategy enabled", category: "feature", type: "boolean", isPublic: true },
            { key: "admin_lead_notification_email", value: String(platform.adminLeadNotificationEmail || "").trim(), label: "Admin lead notification email", category: "integration", type: "string", isPublic: false },
            {
              key: "show_site_wip_banner",
              value: platform.showSiteWipBanner,
              label: "Show work-in-progress site banner",
              category: "feature",
              type: "boolean",
              isPublic: true,
            },
          ],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      swal("Saved", "Platform settings saved successfully.", "success");
    } catch (err) {
      swal("Error", err.response?.data?.error || "Failed to save platform settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* General platform settings */}
      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <h5 className="card-title mb-3">General</h5>
          <Form>
            <Row className="g-4">
              <Col md="12">
                <FormGroup>
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="showSiteWipBanner"
                      name="showSiteWipBanner"
                      checked={platform.showSiteWipBanner}
                      onChange={handlePlatformChange}
                      disabled={saving}
                    />
                    <Label className="custom-control-label" htmlFor="showSiteWipBanner">
                      Show “work in progress” banner (public site + admin)
                    </Label>
                  </div>
                  <span className="form-note">
                    When enabled, visitors see a dismissible notice that the site is under active development and that bugs can be
                    reported. Turn off when the release is stable.
                  </span>
                </FormGroup>
              </Col>

              <Col md="12">
                <FormGroup>
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="googlePlacesEnabled"
                      name="googlePlacesEnabled"
                      checked={platform.googlePlacesEnabled}
                      onChange={handlePlatformChange}
                      disabled={saving}
                    />
                    <Label className="custom-control-label" htmlFor="googlePlacesEnabled">
                      Enable Google Places Autocomplete
                    </Label>
                  </div>
                  <span className="form-note">Enable address autocomplete in the partner registration form (paid service, requires valid API key).</span>
                </FormGroup>
              </Col>

              {platform.googlePlacesEnabled && (
                <Col md="6">
                  <FormGroup>
                    <Label htmlFor="googlePlacesApiKey">Google Places API Key</Label>
                    <Input
                      type="text"
                      id="googlePlacesApiKey"
                      name="googlePlacesApiKey"
                      value={platform.googlePlacesApiKey}
                      onChange={handlePlatformChange}
                      placeholder="AIza…"
                      disabled={saving}
                    />
                    <span className="form-note">
                      Get your key from{" "}
                      <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">
                        Google Cloud Console
                      </a>
                      . Enable the "Places API" for your project.
                    </span>
                  </FormGroup>
                </Col>
              )}

              <Col md="12">
                <FormGroup>
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="blurStrategyEnabled"
                      name="blurStrategyEnabled"
                      checked={platform.blurStrategyEnabled}
                      onChange={handlePlatformChange}
                      disabled={saving}
                    />
                    <Label className="custom-control-label" htmlFor="blurStrategyEnabled">
                      Enable blur overlay on matching results
                    </Label>
                  </div>
                  <span className="form-note">When enabled, clinic recommendations are blurred until the user completes the contact form (lead generation strategy).</span>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label htmlFor="adminLeadNotificationEmail">Admin lead notification email</Label>
                  <Input
                    type="email"
                    id="adminLeadNotificationEmail"
                    name="adminLeadNotificationEmail"
                    value={platform.adminLeadNotificationEmail}
                    onChange={handlePlatformChange}
                    placeholder="admin@example.com"
                    disabled={saving}
                  />
                  <span className="form-note">Receive an email when a new partner lead is submitted. Leave empty to disable.</span>
                </FormGroup>
              </Col>

              <Col md="12">
                <button type="button" className="btn btn-primary" onClick={handlePlatformSave} disabled={saving}>
                  {saving ? "Saving…" : "Save platform settings"}
                </button>
              </Col>
            </Row>
          </Form>
        </div>
      </Card>

    </div>
  );
}

// ─── Quick links & status ─────────────────────────────────────────────────────

export function QuickLinksTab({ adminSettingsPath } = {}) {
  const settingsBase = adminSettingsPath || getPublicUrl("admin-settings");
  const token = useMemo(() => localStorage.getItem("accessToken"), []);
  const [status, setStatus] = useState(null);
  const [regCount, setRegCount] = useState(null);
  const [otpEnabled, setOtpEnabled] = useState(null);
  const [retentionYears, setRetentionYears] = useState(null);
  const [billingIssuerComplete, setBillingIssuerComplete] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      http.get("/api/v1/settings/integrations-status", { headers }),
      http.get("/api/v1/prescription-regulations", { headers }).catch(() => ({ data: { data: [] } })),
      http.get("/api/v1/settings", { headers }),
    ])
      .then(([statusRes, regRes, settingsRes]) => {
        setStatus(statusRes.data?.data || null);
        const regs = regRes.data?.data;
        setRegCount(Array.isArray(regs) ? regs.length : (typeof regs === "object" && regs !== null ? Object.keys(regs).length : 0));

        const settingsData = settingsRes.data?.data || [];
        const map = {};
        settingsData.forEach((s) => { map[s.key] = s.value; });
        const otp = map.tc_otp_enabled;
        setOtpEnabled(otp === true || otp === "true" || otp === 1 || otp === "1");
        setRetentionYears(map.documentRetentionYears != null ? Number(map.documentRetentionYears) : null);
        setBillingIssuerComplete(
          String(map.billing_platform_legal_name || "").trim() !== "" &&
            String(map.billing_platform_legal_address || "").trim() !== "" &&
            String(map.billing_platform_vat_number || "").trim() !== ""
        );
      })
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [token]);

  const CheckItem = ({ ok, children }) => (
    <li className="d-flex align-items-start mb-2">
      <em className={`icon ni ni-${ok ? "check-circle" : "alert-circle"} mr-2 mt-1 text-${ok ? "success" : "warning"}`} style={{ fontSize: "1.1rem" }} />
      <span>{children}</span>
    </li>
  );

  return (
    <div>
      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <h5 className="card-title mb-3">Third-party integrations</h5>
          <p className="text-soft mb-3">
            Stripe, Daily.co, Yousign, Calendly, Google Calendar, and enable/disable toggles are here.{" "}
            <strong>Outbound email</strong> (Gmail OAuth, sender, test) is under Settings → <strong>Email delivery</strong> (see checklist below).
          </p>
          <Link to={`${settingsBase}?tab=integrations`} className="btn btn-primary">
            Open Integrations
          </Link>
        </div>
      </Card>

      {/* Setup checklist */}
      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <h5 className="card-title mb-3">Setup Checklist</h5>
          {loading ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
            </div>
          ) : (
            <ul className="list-unstyled mb-0">
              <CheckItem ok={!!status?.email?.configured}>
                Outbound email (Gmail OAuth){" "}
                {status?.email?.configured
                  ? "credentials present — edit under Settings → Email delivery"
                  : "not configured — set under Settings → Email delivery"}
              </CheckItem>
              <CheckItem ok={integrationReady(status?.stripe)}>
                Stripe{" "}
                {integrationReady(status?.stripe)
                  ? "ready"
                  : status?.stripe?.enabled === false
                    ? "disabled in Admin — enable under Integrations to use payments"
                    : "not configured — required for payments"}
              </CheckItem>
              <CheckItem ok={integrationReady(status?.yousign)}>
                Yousign{" "}
                {integrationReady(status?.yousign)
                  ? "ready"
                  : status?.yousign?.enabled === false
                    ? "disabled in Admin"
                    : "not configured — required for e-signatures"}
              </CheckItem>
              <CheckItem ok={integrationReady(status?.visio)}>
                Video / Daily.co{" "}
                {integrationReady(status?.visio)
                  ? "ready"
                  : status?.visio?.enabled === false
                    ? "disabled in Admin"
                    : "not configured — required for video consultations"}
              </CheckItem>
              <li className="d-flex align-items-start mb-2">
                <em className="icon ni ni-info-fill mr-2 mt-1 text-info" style={{ fontSize: "1.1rem" }} />
                <span>OTP for video access: <strong>{otpEnabled ? "enabled" : "disabled"}</strong></span>
              </li>
              <li className="d-flex align-items-start mb-2">
                <em className={`icon ni ni-${retentionYears != null ? "check-circle" : "alert-circle"} mr-2 mt-1 text-${retentionYears != null ? "success" : "warning"}`} style={{ fontSize: "1.1rem" }} />
                <span>Document retention: <strong>{retentionYears != null ? `${retentionYears} year(s)` : "not set"}</strong></span>
              </li>
              <CheckItem ok={regCount != null && regCount > 0}>
                Prescription regulations: <strong>{regCount != null ? `${regCount} configured` : "loading…"}</strong>
                {(regCount === 0) && " — at least one regulation is recommended"}
              </CheckItem>
              <CheckItem ok={billingIssuerComplete}>
                Invoice PDF issuer (legal name, address, VAT):{" "}
                {billingIssuerComplete ? (
                  <strong>configured</strong>
                ) : (
                  <>
                    <strong>incomplete</strong> — set under{" "}
                    <Link to={`${settingsBase}?tab=billing`}>Billing & invoices</Link>
                  </>
                )}
              </CheckItem>
            </ul>
          )}
        </div>
      </Card>

      {/* Quick links */}
      <Card className="card-bordered">
        <div className="card-inner">
          <h5 className="card-title mb-3">Quick Links</h5>
          <ul className="list-unstyled mb-0">
            {[
              { label: "Integrations", to: `${settingsBase}?tab=integrations` },
              { label: "Teleconsultation", to: `${settingsBase}?tab=teleconsultation` },
              { label: "Billing & invoices", to: `${settingsBase}?tab=billing` },
              { label: "Marketing & security", to: `${settingsBase}?tab=marketing` },
              { label: "Prescription regulations", to: getPublicUrl("prescription-regulations") },
              { label: "Email templates", to: getPublicUrl("email-templates-management") },
              { label: "Email delivery (Gmail + sender)", to: `${settingsBase}?tab=email` },
            ].map(({ label, to }) => (
              <li key={to} className="mb-2">
                <Link to={to} className="btn btn-outline-light btn-sm">
                  <em className="icon ni ni-arrow-right mr-1" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </div>
  );
}

export default function PlatformSetupLegacyRedirect() {
  return <Redirect to={{ pathname: getPublicUrl("admin-settings"), search: "?tab=teleconsultation" }} />;
}
